import { PlacementWithLogical, Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { AlarmClockIcon, CloseIcon, ReloadIcon } from '@sketchnote/icons';
import {
  CalendarProps,
  CalendarReminder,
  CalendarRepeat,
  CalendarTabs,
  OnChangeReminderCallback,
  OnChangeRepeatCallback,
} from '@sketchnote/shared-components';
import { Calendar } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import { format } from 'date-fns';
import isSameDay from 'date-fns/isSameDay';
import * as React from 'react';
import type { OnChangeDateRangeCallback } from 'react-calendar';

import { KanbanColumnOption } from '../types/tasks';

const { useMemo } = React;
interface DateRangePickerProps {
  value: [Date] | [Date, Date] | null;
  onChange: OnChangeDateRangeCallback;
  onDone?: () => void;
  inputClassName?: string;
  placeholderText?: string;
  dateFormat?: string;
  initialTab?: CalendarTabs;
  readOnly?: boolean;
  disableEndDate?: CalendarProps['disableEndDate'];
  reminderOffset?: CalendarReminder;
  onChangeReminder?: OnChangeReminderCallback;
  onSelectKanbanColumn?: (column: string) => void;
  selectKanbanColumnOptions?: KanbanColumnOption[];
  onChangeRepeat?: OnChangeRepeatCallback;
  onFocus?: (e) => void;
  onBlur?: (e) => void;
  triggerButton?: React.ReactNode;
  onClearDateRange?: () => void;
  showReminder?: boolean;
  showRecurring?: boolean;
  recurringData?: CalendarRepeat;
  show?: CalendarProps['show'];
  minDate?: Date;
  dateLabels?: [string, string];
  endDateFirst?: boolean;
  CustomPopoverTrigger?: React.FC<{ showReminder: boolean; value: string; showRecurring: boolean }>;
  popoverPlacement?: PlacementWithLogical;
  readOnlyTooltipText?: string;
}

export const DateRangePicker = ({
  value,
  onChange,
  onDone,
  inputClassName,
  placeholderText = 'Select dates',
  dateFormat = 'do MMM yyyy',
  initialTab = 'date',
  readOnly = false,
  showRecurring = false,
  showReminder = false,
  disableEndDate = false,
  show,
  reminderOffset,
  onChangeReminder,
  onChangeRepeat,
  onFocus,
  onBlur,
  onClearDateRange,
  recurringData,
  minDate,
  endDateFirst,
  CustomPopoverTrigger,
  popoverPlacement,
  readOnlyTooltipText,
}: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const open = () => setIsOpen(!isOpen);
  const close = () => setIsOpen(false);

  const handleDatesForInput = (values) => {
    if (!values) return placeholderText;
    else if (values.length === 1) return `${format(values[0], dateFormat)}`;
    else if (values.length > 1) {
      if (isSameDay(values[0], values[1])) {
        return `${format(values[0], dateFormat)} | ${format(values[0], 'p')} - ${format(
          values[1],
          'p'
        )}`;
      } else {
        return `${format(values[0], dateFormat)} - ${format(values[1], dateFormat)}`;
      }
    }
    return placeholderText;
  };

  const handleDoneClick = () => {
    if (onDone) onDone();
    setIsOpen(false);
  };

  const optionalProps = useMemo(() => {
    let tempOptionalProps = {};
    if (showRecurring) {
      tempOptionalProps = {
        recurringData,
      };
    }

    if (showReminder) {
      tempOptionalProps = {
        ...tempOptionalProps,
        reminderOffset,
        onChangeReminder,
      };
    }
    return tempOptionalProps;
  }, [recurringData]);

  return (
    <Popover
      isOpen={!readOnly && isOpen}
      onClose={close}
      placement={popoverPlacement || (showReminder || showRecurring ? 'left' : 'auto')}
    >
      <PopoverTrigger>
        <Tippy disabled={!readOnly || !readOnlyTooltipText} content={readOnlyTooltipText}>
          {CustomPopoverTrigger ? (
            <button className="cursor-pointer w-full" onClick={open}>
              <CustomPopoverTrigger
                showReminder={!!(showReminder && reminderOffset && reminderOffset !== 0)}
                showRecurring={!!(showRecurring && recurringData?.type)}
                value={handleDatesForInput(value)}
              />
            </button>
          ) : (
            <button
              onClick={open}
              className={classNames({
                'flex justify-between items-center': true,
                'text-gray-500': !value,
                [inputClassName || '']: !!inputClassName,
              })}
            >
              <span className="text-sm">{handleDatesForInput(value)}</span>
              <div className="flex">
                <div className="mt-2 mr-8">
                  {showReminder && reminderOffset && reminderOffset !== 0 ? (
                    <AlarmClockIcon wrapperClass="text-blue-500 " />
                  ) : showRecurring && recurringData?.type ? (
                    <ReloadIcon className="text-blue-500" />
                  ) : (
                    <></>
                  )}
                </div>

                {onClearDateRange && !!value?.length && (
                  <button
                    onClick={(e) => {
                      onClearDateRange();
                      e.stopPropagation();
                    }}
                  >
                    <CloseIcon className="text-gray-400" size={12} />
                  </button>
                )}
              </div>
            </button>
          )}
        </Tippy>
      </PopoverTrigger>
      <PopoverContent
        zIndex={4}
        onBlur={(e) => {
          onBlur && onBlur(e);
        }}
        onFocus={(e) => {
          onFocus && onFocus(e);
        }}
      >
        {isOpen && (
          <Calendar
            {...optionalProps}
            value={value}
            show={show}
            onChange={onChange}
            onDone={handleDoneClick}
            initialTab={initialTab}
            showReminder={showReminder}
            reminderOffset={reminderOffset}
            showRecurring={showRecurring}
            disableEndDate={disableEndDate}
            onChangeRepeat={onChangeRepeat}
            minDate={minDate}
            endDateFirst={endDateFirst}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
